import { trackTicket, submitTrackingReply } from '../api-client'
import type { EventCallback, TrackedTicketResult } from '../types'

type T = (key: string) => string

export function createTrackerView(
  t: T,
  onTracked: EventCallback,
  onReplySent: EventCallback,
  onError: EventCallback,
  onBack?: () => void,
  prefillToken?: string,
): HTMLElement {
  const container = document.createElement('div')
  container.className = 'rqd-tracker'

  if (prefillToken) {
    renderLoading(container, t)
    doTrack(prefillToken, container, t, onTracked, onReplySent, onError, onBack)
  } else {
    renderTokenInput(container, t, onTracked, onReplySent, onError, onBack)
  }

  return container
}

function renderTokenInput(
  container: HTMLElement,
  t: T,
  onTracked: EventCallback,
  onReplySent: EventCallback,
  onError: EventCallback,
  onBack?: () => void,
) {
  while (container.firstChild) container.removeChild(container.firstChild)

  const group = document.createElement('div')
  group.className = 'rqd-form-group'

  const label = document.createElement('label')
  label.className = 'rqd-label'
  label.textContent = t('tracker.title')
  group.appendChild(label)

  const input = document.createElement('input')
  input.className = 'rqd-input'
  input.placeholder = t('tracker.tokenPlaceholder')
  input.type = 'text'
  group.appendChild(input)

  const err = document.createElement('div')
  err.className = 'rqd-error-text'
  err.style.display = 'none'
  group.appendChild(err)

  container.appendChild(group)

  const btn = document.createElement('button')
  btn.className = 'rqd-btn rqd-btn-primary'
  btn.textContent = t('tracker.submit')
  btn.addEventListener('click', () => {
    const token = input.value.trim()
    if (!token) {
      err.textContent = t('error.required')
      err.style.display = 'block'
      return
    }
    err.style.display = 'none'
    btn.disabled = true
    btn.textContent = t('tracker.tracking')
    doTrack(token, container, t, onTracked, onReplySent, onError, onBack)
  })
  container.appendChild(btn)
}

function renderLoading(container: HTMLElement, t: T) {
  while (container.firstChild) container.removeChild(container.firstChild)
  const p = document.createElement('p')
  p.textContent = t('tracker.tracking')
  p.style.textAlign = 'center'
  p.style.color = 'var(--rqd-text-secondary)'
  container.appendChild(p)
}

async function doTrack(
  token: string,
  container: HTMLElement,
  t: T,
  onTracked: EventCallback,
  onReplySent: EventCallback,
  onError: EventCallback,
  onBack?: () => void,
) {
  try {
    const result = await trackTicket(token)
    onTracked(result)
    renderTicketDetail(container, token, result, t, onReplySent, onError, onBack)
  } catch (err) {
    onError(err)
    renderTokenInput(container, t, onTracked, onReplySent, onError, onBack)
  }
}

function renderTicketDetail(
  container: HTMLElement,
  token: string,
  ticket: TrackedTicketResult,
  t: T,
  onReplySent: EventCallback,
  onError: EventCallback,
  onBack?: () => void,
) {
  while (container.firstChild) container.removeChild(container.firstChild)

  if (onBack) {
    const backBtn = document.createElement('button')
    backBtn.className = 'rqd-btn rqd-btn-secondary'
    backBtn.textContent = t('tracker.back')
    backBtn.style.marginBottom = '12px'
    backBtn.addEventListener('click', onBack)
    container.appendChild(backBtn)
  }

  // Ticket info
  const info = document.createElement('div')
  info.className = 'rqd-ticket-info'

  const titleRow = document.createElement('div')
  titleRow.style.fontWeight = '600'
  titleRow.style.marginBottom = '8px'
  titleRow.textContent = `${ticket.ticketNumber} — ${ticket.title}`
  info.appendChild(titleRow)

  for (const [label, value] of [
    [t('tracker.status'), ticket.status],
    [t('tracker.priority'), ticket.priority],
    [t('tracker.created'), new Date(ticket.createdAt).toLocaleDateString()],
  ]) {
    const row = document.createElement('div')
    row.className = 'rqd-ticket-row'
    const lbl = document.createElement('span')
    lbl.className = 'rqd-ticket-label'
    lbl.textContent = label
    row.appendChild(lbl)
    const badge = document.createElement('span')
    badge.className = 'rqd-badge'
    badge.textContent = value
    row.appendChild(badge)
    info.appendChild(row)
  }
  container.appendChild(info)

  // Replies
  const repliesLabel = document.createElement('div')
  repliesLabel.className = 'rqd-label'
  repliesLabel.textContent = t('tracker.replies')
  repliesLabel.style.marginBottom = '8px'
  container.appendChild(repliesLabel)

  if (ticket.replies.length === 0) {
    const noReplies = document.createElement('p')
    noReplies.textContent = t('tracker.noReplies')
    noReplies.style.color = 'var(--rqd-text-secondary)'
    noReplies.style.fontSize = '13px'
    container.appendChild(noReplies)
  } else {
    for (const reply of ticket.replies) {
      const replyEl = document.createElement('div')
      replyEl.className = 'rqd-reply'

      const header = document.createElement('div')
      header.className = 'rqd-reply-header'
      const authorSpan = document.createElement('span')
      authorSpan.textContent = reply.authorName
      if (reply.isStaff) authorSpan.className = 'rqd-reply-staff'
      header.appendChild(authorSpan)
      const dateSpan = document.createElement('span')
      dateSpan.textContent = new Date(reply.createdAt).toLocaleString()
      header.appendChild(dateSpan)
      replyEl.appendChild(header)

      const body = document.createElement('div')
      body.className = 'rqd-reply-body'
      body.textContent = reply.body
      replyEl.appendChild(body)

      container.appendChild(replyEl)
    }
  }

  // Reply form
  const replyGroup = document.createElement('div')
  replyGroup.className = 'rqd-form-group'
  replyGroup.style.marginTop = '12px'

  const textarea = document.createElement('textarea')
  textarea.className = 'rqd-textarea'
  textarea.placeholder = t('tracker.replyPlaceholder')
  textarea.rows = 3
  replyGroup.appendChild(textarea)
  container.appendChild(replyGroup)

  const sendBtn = document.createElement('button')
  sendBtn.className = 'rqd-btn rqd-btn-primary'
  sendBtn.textContent = t('tracker.sendReply')
  sendBtn.addEventListener('click', async () => {
    const body = textarea.value.trim()
    if (!body) return

    sendBtn.disabled = true
    sendBtn.textContent = t('tracker.sending')

    try {
      await submitTrackingReply(token, body)
      onReplySent({ token, body })
      // Refresh ticket detail
      const updated = await trackTicket(token)
      renderTicketDetail(container, token, updated, t, onReplySent, onError, onBack)
    } catch (err) {
      onError(err)
      sendBtn.disabled = false
      sendBtn.textContent = t('tracker.sendReply')
    }
  })
  container.appendChild(sendBtn)
}
